import React, { useEffect, useRef, useState } from 'react';
import './timeStrip.scss';
import ArtworkDataService from '../../services/ArtworkService';
import { useLanguage } from '../../LanguageContext';

/**
 * Barra del tempo della mappa spazio-temporale.
 * - istogramma dei secoli come superficie di navigazione
 * - finestra temporale trascinabile (pointer events nativi)
 * - pulsante Riproduci che anima i secoli in sequenza
 * - tacche narrative degli snodi storici bizantini
 */

const CENTURIES = [4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15];
const ROMAN = {
  4: 'IV', 5: 'V', 6: 'VI', 7: 'VII', 8: 'VIII', 9: 'IX',
  10: 'X', 11: 'XI', 12: 'XII', 13: 'XIII', 14: 'XIV', 15: 'XV',
};
const SPAN_START = 301;
const SPAN_END = 1500;

const EVENTS = [
  { year: 330, it: 'Fondazione di Costantinopoli', en: 'Foundation of Constantinople' },
  { year: 726, it: "Inizio dell'iconoclastia", en: 'Beginning of Iconoclasm' },
  { year: 1054, it: "Scisma d'Oriente", en: 'East-West Schism' },
  { year: 1204, it: 'Quarta Crociata: sacco di Costantinopoli', en: 'Fourth Crusade: sack of Constantinople' },
  { year: 1453, it: 'Caduta di Costantinopoli', en: 'Fall of Constantinople' },
];

const TimeStrip = ({ window: win, onChange, activeCount }) => {
  const { selectedLanguage } = useLanguage();
  const it = selectedLanguage === 'it';
  const [histogram, setHistogram] = useState({});
  const [playing, setPlaying] = useState(false);
  const dragFrom = useRef(null);

  useEffect(() => {
    ArtworkDataService.getDateHistogram()
      .then((rows) => {
        const map = {};
        (rows || []).forEach((r) => { map[r.century] = r.count; });
        setHistogram(map);
      })
      .catch(() => { /* decorativo */ });
  }, []);
  const maxCount = Math.max(1, ...Object.values(histogram));

  const fullWindow = win.from === 4 && win.to === 15;
  const setWindow = (a, b) =>
    onChange({ from: Math.min(a, b), to: Math.max(a, b) });
  const resetWindow = () => { setPlaying(false); setWindow(4, 15); };

  // ▶ Riproduci: un secolo alla volta, dal IV al XV
  useEffect(() => {
    if (!playing) return undefined;
    let c = 4;
    onChange({ from: c, to: c });
    const id = setInterval(() => {
      c += 1;
      if (c > 15) {
        setPlaying(false);
        return;
      }
      onChange({ from: c, to: c });
    }, 1800);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playing]);

  // selezione con trascinamento sulle celle (il prop "window" oscura il
  // window globale: si ascolta sul document)
  useEffect(() => {
    const up = () => { dragFrom.current = null; };
    document.addEventListener('pointerup', up);
    return () => document.removeEventListener('pointerup', up);
  }, []);

  const onCellDown = (n) => {
    setPlaying(false);
    dragFrom.current = n;
    setWindow(n, n);
  };
  const onCellEnter = (n) => {
    if (dragFrom.current !== null) {
      setWindow(dragFrom.current, n);
    }
  };

  const isActive = (n) => n >= win.from && n <= win.to;

  const caption = fullWindow
    ? (it ? 'Tutti i secoli' : 'All centuries')
    : win.from === win.to
      ? `${ROMAN[win.from]} ${it ? 'secolo' : 'century'}`
      : `${ROMAN[win.from]} – ${ROMAN[win.to]} ${it ? 'secolo' : 'century'}`;

  return (
    <div className="time-strip">
      <div className="time-strip-controls">
        <button
          type="button"
          className={`btn btn-sm ${playing ? 'btn-dark' : 'btn-outline-dark'}`}
          onClick={() => setPlaying((p) => !p)}
        >
          {playing ? '⏸' : '▶'} {it ? (playing ? 'Pausa' : 'Riproduci') : (playing ? 'Pause' : 'Play')}
        </button>
        <span className="time-strip-caption">
          {caption} · {activeCount} {it
            ? (activeCount === 1 ? 'opera' : 'opere')
            : (activeCount === 1 ? 'work' : 'works')}
        </span>
        {!fullWindow && (
          <button
            type="button"
            className="btn btn-sm btn-link time-strip-reset"
            onClick={resetWindow}
          >
            ✕ {it ? 'tutti i secoli' : 'all centuries'}
          </button>
        )}
      </div>

      <div className="time-strip-track">
        {EVENTS.map((ev) => (
          <span
            key={ev.year}
            className="time-tick"
            style={{
              left: `${((ev.year - SPAN_START) / (SPAN_END - SPAN_START + 1)) * 100}%`,
            }}
            title={`${ev.year} · ${it ? ev.it : ev.en}`}
          >
            ▾
          </span>
        ))}
        <div className="time-cells">
          {CENTURIES.map((n) => {
            const count = histogram[n] || 0;
            return (
              <div
                key={n}
                className={`time-cell ${isActive(n) ? 'active' : ''}`}
                onPointerDown={() => onCellDown(n)}
                onPointerEnter={() => onCellEnter(n)}
                onDoubleClick={resetWindow}
                title={`${ROMAN[n]} sec.: ${count} ${count === 1 ? 'opera' : 'opere'}`}
              >
                <div
                  className="time-bar"
                  style={{ height: `${Math.round((count / maxCount) * 34)}px` }}
                />
                <div className="time-label">{ROMAN[n]}</div>
              </div>
            );
          })}
        </div>
      </div>
      <small className="text-muted time-strip-hint">
        {it
          ? 'Tocca un secolo o trascina per scegliere un periodo; doppio click per tornare a tutti i secoli.'
          : 'Tap a century or drag to pick a period; double-click to reset.'}
      </small>
    </div>
  );
};

export default TimeStrip;
